import { createSelector } from '@reduxjs/toolkit';
import { fetchBidLimitBalance } from '@/redux/api/bidLimit';
import { getAuthToken, getBidderId, isUserLoggedIn } from './user';
import { getCatalogRegistration } from './catalogRegistration';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import cloneDeep from 'lodash/cloneDeep';
import difference from 'lodash/difference';
import isEmpty from 'lodash/isEmpty';
import ms from 'ms';
import union from 'lodash/union';

/* Action Types */
const LOAD_BID_LIMIT_FAIL = 'la/domain/bidLimit/LOAD_FAIL';
export const LOAD_BID_LIMIT_REQUEST = 'la/domain/bidLimit/LOAD_REQUEST';
export const LOAD_BID_LIMIT_SUCCESS = 'la/domain/bidLimit/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('1s');

export type State = {
    byId: { [catalogId: number]: any };
    loaded: { [catalogId: number]: number };
    loading: number[];
};

export const DEFAULT_STATE: State = {
    byId: {},
    loaded: {},
    loading: [],
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}): State {
    let existing: State['byId'];
    let loaded: State['loaded'];
    let loading: State['loading'];
    let time: number;

    switch (action.type) {
        case LOAD_BID_LIMIT_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.catalogId]),
            };
        case LOAD_BID_LIMIT_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
            };
        case LOAD_BID_LIMIT_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded };
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;
            existing[action.payload.catalogId] = { balance: action.payload.bidLimitRemaining };
            loaded[action.payload.catalogId] = time;
            loading = difference(loading, [action.payload.catalogId]);
            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.bidLimit;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

export const getIsBidLimitLoaded = createSelector([loadedSelector, idSelector], (loaded, id) =>
    loaded.hasOwnProperty(id)
);

export const getTotalBidLimitBalance = (state: any, catalogId: number) => {
    const byId = byIdSelector(state);
    const catalogRegistration = getCatalogRegistration(state, catalogId);
    if (!catalogRegistration.limited) {
        return Infinity;
    }

    const data = byId[catalogId] || { balance: 0 };
    return data.balance || 0;
};

export const getBidLimitBalance = (state: any, catalogId: number) => {
    const byId = byIdSelector(state);
    const catalogRegistration = getCatalogRegistration(state, catalogId);
    if (!catalogRegistration.limited) {
        return Infinity;
    }

    const data = byId[catalogId] || { balance: 0 };
    const serverBalance = data.balance || 0;

    return Math.max(serverBalance, 0);
};

const getLoadTime = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

const isLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

const shouldFetch = (state, catalogId) => {
    const item = getBidLimitBalance(state, catalogId);
    const loaded = getLoadTime(state, catalogId);
    const loading = isLoading(state, catalogId);
    const authToken = getAuthToken(state);
    const bidderId = getBidderId(state);

    if (!authToken) {
        return false;
    }
    if (bidderId === -1) {
        return false;
    }
    if (!catalogId) {
        return false;
    }
    if (!isEmpty(item)) {
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    return !loading;
};

type FetchBidLimitProps = {
    catalogId: number;
    force?: boolean;
    itemId?: number;
};

/* ACTION CREATORS */
export const fetchBidLimitBalanceIfNeeded =
    ({ catalogId, force = false, itemId }: FetchBidLimitProps) =>
    async (dispatch: Function, getState: Function) => {
        const state = getState();
        const loggedIn = isUserLoggedIn(state);
        const { registered } = getCatalogRegistration(state, catalogId);
        if ((force || shouldFetch(state, catalogId)) && loggedIn && registered) {
            const bidderId = getBidderId(state);
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            try {
                dispatch({
                    meta: { actionTime: Date.now() },
                    payload: catalogId,
                    type: LOAD_BID_LIMIT_REQUEST,
                });
                const response = await fetchBidLimitBalance({ authToken, bidderId, catalogId, deployment, itemId });
                dispatch({
                    meta: { actionTime: Date.now(), catalogId },
                    payload: response.payload,
                    type: LOAD_BID_LIMIT_SUCCESS,
                });
            } catch (error) {
                dispatch({
                    error: true,
                    meta: { catalogId },
                    payload: error,
                    type: LOAD_BID_LIMIT_FAIL,
                });
            }
        } else {
            return Promise.resolve();
        }
    };
